import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtInt } from "@/lib/api";
import { saveOrDownload } from "@/lib/downloadFolder";
import { applyDiagonalWatermark, hexToRgb } from "@/lib/pdfBranding";
import { isCostRow } from "@/lib/budgetRows";

const eur = (v) => (v === null ? "—" : `€ ${fmtInt(v)}`);
const fmtDelta = (v) => (v === null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}€ ${fmtInt(Math.abs(v))}`);
const fmtPct = (v) => (v === null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)}%`);
const GREEN = [0, 160, 71];
const RED = [192, 0, 0];

// PDF 'Begroting vs realisatie' (liggend): kop met huisstijl, drie kaarten netto resultaat,
// tabel met Toelichting-kolom en legenda. Zelfde rijen als de tabel in de app.
export async function downloadRealisationPdf({ t, club, clubId, year, rows, budget, actual, userTier }) {
  const doc = new jsPDF({ orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const brandRgb = hexToRgb(club?.brand_color || "#0047FF");
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, pageW, 42, "F");
  let logo = false;
  if (club?.logo_data_uri) {
    try { doc.addImage(club.logo_data_uri, "AUTO", 14, 9, 24, 24, undefined, "FAST"); logo = true; } catch { /* balkje hieronder */ }
  }
  if (!logo) { doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]); doc.rect(14, 10, 8, 22, "F"); }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text("MEERJARENPLAN ONLINE · BEGROTING VS REALISATIE", logo ? 44 : 28, 19);
  doc.setFontSize(18);
  doc.text(`${club?.name || "Club"} · ${year}`, logo ? 44 : 28, 30);
  doc.setFontSize(10);
  const today = new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(today, pageW - 14 - doc.getTextWidth(today), 36);

  const val = (row, obj) => (obj ? row.value(obj) : null);
  const nettoRow = rows.find((r) => r.key === "netto");
  const nettoB = nettoRow ? val(nettoRow, budget) : null;
  const nettoA = nettoRow ? val(nettoRow, actual) : null;
  const nettoD = nettoB === null || nettoA === null ? null : nettoA - nettoB;
  const cards = [
    [t("Netto exploitatieresultaat begroot"), eur(nettoB), [10, 10, 10]],
    [t("Netto exploitatieresultaat gerealiseerd"), eur(nettoA), [10, 10, 10]],
    [t("Verschil"), fmtDelta(nettoD), nettoD === null || nettoD === 0 ? [10, 10, 10] : nettoD > 0 ? GREEN : RED],
  ];
  const cardW = (pageW - 28 - 8) / 3;
  cards.forEach(([label, value, colour], i) => {
    const x = 14 + i * (cardW + 4);
    doc.setDrawColor(10, 10, 10); doc.setLineWidth(0.6); doc.rect(x, 48, cardW, 18);
    doc.setFontSize(7); doc.setTextColor(90, 90, 90); doc.text(label.toUpperCase(), x + 3, 54);
    doc.setFontSize(13); doc.setTextColor(colour[0], colour[1], colour[2]); doc.text(value, x + 3, 62);
  });

  const notes = budget?.variance_notes || {};
  const body = rows.map((row) => {
    const b = val(row, budget);
    const a = val(row, actual);
    const de = b === null || a === null ? null : a - b;
    const dp = b === null || a === null || !b ? null : ((a - b) / Math.abs(b)) * 100;
    const favourable = de === null || de === 0 ? null : isCostRow(row) ? de < 0 : de > 0;
    return {
      kind: row.kind, strong: row.strong, favourable, big: dp !== null && Math.abs(dp) >= 20,
      cells: [row.kind === "sub" || row.kind === "sys" ? `    ${row.label}` : row.label, eur(b), eur(a), fmtDelta(de), fmtPct(dp), notes[row.key] || ""],
    };
  });
  autoTable(doc, {
    startY: 72,
    head: [[t("Post"), `${t("Begroting")} ${year}`, `${t("Realisatie")} ${year}`, t("Verschil €"), t("Verschil %"), t("Toelichting")]],
    body: body.map((r) => r.cells),
    styles: { fontSize: 8.5, halign: "right", cellPadding: 1.6 },
    headStyles: { fillColor: [10, 10, 10], halign: "center", fontSize: 8.5 },
    columnStyles: {
      0: { halign: "left", fontStyle: "bold", cellWidth: 58 },
      1: { cellWidth: 30 }, 2: { cellWidth: 30, fillColor: [238, 238, 238] }, 3: { cellWidth: 30 }, 4: { cellWidth: 24 },
      5: { halign: "left", fontStyle: "normal" },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const meta = body[data.row.index];
      if (!meta) return;
      if (meta.kind === "sub" || meta.kind === "sys") {
        data.cell.styles.fontStyle = "italic";
        data.cell.styles.textColor = [90, 90, 90];
      } else if (meta.kind === "computed") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [230, 236, 255];
      }
      if ((data.column.index === 3 || data.column.index === 4) && meta.favourable !== null) {
        data.cell.styles.textColor = meta.favourable ? GREEN : RED;
        if (meta.big) data.cell.styles.fontStyle = "bold";
      }
      if (data.column.index === 5) {
        data.cell.styles.fontStyle = "normal";
        data.cell.styles.textColor = [10, 10, 10];
        data.cell.styles.fontSize = 8;
      }
    },
  });

  let y = ((doc.lastAutoTable && doc.lastAutoTable.finalY) || 72) + 6;
  const pageH = doc.internal.pageSize.getHeight();
  const addText = (text, colour = [90, 90, 90]) => {
    const lines = doc.splitTextToSize(text, pageW - 28);
    if (y + lines.length * 4 > pageH - 12) { doc.addPage(); y = 20; }
    doc.setFontSize(8.5); doc.setTextColor(colour[0], colour[1], colour[2]); doc.text(lines, 14, y);
    y += lines.length * 4 + 3;
  };
  addText(t("Verschil = realisatie − begroting. Groen = gunstig (meer inkomsten of lagere kosten dan begroot), rood = ongunstig; vet zodra de afwijking ≥ 20% is."));
  if ((budget?.notes || "").trim()) addText(`${t("Toelichting begroting")} ${year}: ${budget.notes.trim()}`, [10, 10, 10]);
  if (userTier === "TRIAL") applyDiagonalWatermark(doc, "PROEF");

  const filename = `${(club?.name || "club").replace(/[\\/:*?"<>|]/g, "-")}-begroting-vs-realisatie-${year}.pdf`;
  await saveOrDownload(doc.output("blob"), filename, clubId);
}
